import { GRID_H, GRID_W, PLAYER_COLORS, type FruitKind } from "../src/protocol";

/** How each fruit kind looks, by index. The last one is the bonus fruit. */
export const FRUIT_GLYPHS = ["\u{1F34E}", "\u{1F34C}", "\u{1F352}"] as const; // apple, banana, cherries

/** What one frame shows: straight from a "tick" message. */
export interface Frame {
  /** [slot, cells head-first]. */
  snakes: [number, number[]][];
  /** [cell, kind]. */
  food: [number, FruitKind][];
}

const EMPTY: Frame = { snakes: [], food: [] };

/**
 * Canvas arena. Cells are drawn as-is on every server step (no interpolation): at
 * ~8 steps per second that reads as the classic stepped movement.
 */
export class ArenaView {
  readonly el = document.createElement("canvas");
  private readonly ctx = this.el.getContext("2d")!;
  private cell = 10;
  private frame: Frame = EMPTY;
  private mySlot = -1;

  constructor(private readonly host: HTMLElement) {
    this.el.className = "arena-canvas";
    this.el.setAttribute("role", "img");
    host.append(this.el);
    new ResizeObserver(() => this.resize()).observe(host);
    // Colors come from CSS variables, which change with the color scheme.
    matchMedia("(prefers-color-scheme: dark)").addEventListener("change", () => this.draw());
    this.resize();
  }

  setLabel(label: string): void {
    this.el.setAttribute("aria-label", label);
  }

  show(frame: Frame, mySlot: number): void {
    this.frame = frame;
    this.mySlot = mySlot;
    this.draw();
  }

  clear(): void {
    this.show(EMPTY, this.mySlot);
  }

  private resize(): void {
    const width = this.host.clientWidth;
    if (width === 0) return;
    this.cell = Math.max(4, Math.floor(width / GRID_W));
    const cssW = this.cell * GRID_W;
    const cssH = this.cell * GRID_H;
    const dpr = window.devicePixelRatio || 1;
    this.el.style.width = `${cssW}px`;
    this.el.style.height = `${cssH}px`;
    this.el.width = Math.round(cssW * dpr);
    this.el.height = Math.round(cssH * dpr);
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.draw();
  }

  private draw(): void {
    const { ctx, cell } = this;
    const style = getComputedStyle(this.el);
    const ink = style.getPropertyValue("--ink").trim() || "#1f1d1a";
    const floor = style.getPropertyValue("--arena-floor").trim() || "#fffdf8";
    const tile = style.getPropertyValue("--arena-tile").trim() || "#f3eee2";

    ctx.fillStyle = floor;
    ctx.fillRect(0, 0, cell * GRID_W, cell * GRID_H);
    // Checkerboard: makes speed and distance readable without grid lines.
    ctx.fillStyle = tile;
    for (let y = 0; y < GRID_H; y++) {
      for (let x = y % 2; x < GRID_W; x += 2) ctx.fillRect(x * cell, y * cell, cell, cell);
    }

    // Fruit is drawn as emoji, a little larger than its cell so it reads at small sizes.
    ctx.font = `${Math.round(cell * 1.05)}px "Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillStyle = ink;
    for (const [index, kind] of this.frame.food) {
      const [x, y] = this.xy(index);
      ctx.fillText(FRUIT_GLYPHS[kind] ?? FRUIT_GLYPHS[0], x + cell / 2, y + cell / 2 + cell * 0.06);
    }

    // Own snake last, so it stays on top where two overlap for a frame.
    const ordered = [...this.frame.snakes].sort((a, b) => Number(a[0] === this.mySlot) - Number(b[0] === this.mySlot));
    for (const [slot, cells] of ordered) this.drawSnake(slot, cells, ink);
  }

  private drawSnake(slot: number, cells: number[], ink: string): void {
    const { ctx, cell } = this;
    const mine = slot === this.mySlot;
    const gap = Math.max(0.5, cell * 0.08);
    const radius = cell * 0.3;

    ctx.fillStyle = PLAYER_COLORS[slot % PLAYER_COLORS.length]!;
    ctx.strokeStyle = ink;
    ctx.lineWidth = Math.max(1.5, cell * 0.16);
    for (let i = cells.length - 1; i >= 0; i--) {
      const [x, y] = this.xy(cells[i]!);
      const inset = i === 0 ? 0 : gap;
      ctx.beginPath();
      ctx.roundRect(x + inset, y + inset, cell - inset * 2, cell - inset * 2, radius);
      ctx.fill();
      // The outline is how you find yourself among twelve snakes.
      if (mine) ctx.stroke();
    }

    const head = cells[0];
    if (head === undefined) return;
    const [hx, hy] = this.xy(head);
    ctx.fillStyle = "#ffffff";
    ctx.beginPath();
    ctx.arc(hx + cell * 0.32, hy + cell * 0.38, cell * 0.13, 0, Math.PI * 2);
    ctx.arc(hx + cell * 0.68, hy + cell * 0.38, cell * 0.13, 0, Math.PI * 2);
    ctx.fill();
  }

  private xy(index: number): [number, number] {
    return [(index % GRID_W) * this.cell, Math.floor(index / GRID_W) * this.cell];
  }
}
